import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useKidAuth } from '../../contexts/KidAuthContext';
import { dbKidData as db } from '../../services/supabase';
import { formatCurrency, parseCurrency } from '../../utils/currency';
import { EXPENSE_CATEGORIES } from '../../utils/constants';
import { useBalance } from '../../hooks/useBalance';
import '../../styles/brand-typography.css';
import './KidMoneyFlow.css';
import KidLayout from '../../components/Layout/KidLayout';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import MyPiggyIcon from '../../components/UI/MyPiggyIcon';

const AddExpense = () => {
  const navigate = useNavigate();
  const {
    currentKid,
    isFamilyInactive,
    canWrite: familyAccessCanWrite,
  } = useKidAuth();

  const isWriteLocked =
    isFamilyInactive ||
    familyAccessCanWrite === false;
  const {
    balance,
    loading: balanceLoading,
    error: balanceError,
  } = useBalance(currentKid?.id, currentKid?.family_id, 'kid');

  const [selectedCategory, setSelectedCategory] = useState('');
  const [formData, setFormData] = useState({
    amount: '',
    note: '',
    needWant: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isWriteLocked) {
      setError(
        'Your MyPiggy is currently read-only.'
      );
      return;
    }
    setLoading(true);
    setError('');

    try {
      if (balanceLoading) {
        throw new Error('We are still checking your Pocket Money. Please try again in a moment.');
      }

      if (balanceError) {
        throw new Error('We could not check your Pocket Money right now. Please try again before saving this expense.');
      }

      const amountCents = parseCurrency(formData.amount);

      if (amountCents <= 0) {
        throw new Error('Amount must be greater than $0');
      }

      if (amountCents > balance) {
        throw new Error(
          `You only have ${formatCurrency(balance)} available to spend. Try entering a smaller amount.`
        );
      }

      await db.createTransaction({
        family_id: currentKid.family_id,
        child_id: currentKid.id,
        type: 'expense',
        amount_cents: amountCents,
        category: selectedCategory,
        need_want: formData.needWant,
        note: formData.note || undefined,
        approval_status: 'approved',
        created_by: 'child'
      });

      navigate('/kid/dashboard');
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const getCategoryEmoji = (category) => {
    const emojis = {
      snacks: '🍿',
      toys: '🧸',
      games: '🎮',
      clothes: '👕',
      school: '📚',
      other: '📦'
    };
    return emojis[category] || '📦';
  };

  if (!currentKid) {
    return null;
  }

  return (
    <KidLayout
      className="mypiggy-brand kid-money-flow"
      title="Spent Money"
      avatar={currentKid.avatar}
      showBack
    >
      <div className="space-y-6">
        <Card className="p-4 bg-green-50 border border-green-100">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-base font-extrabold text-teal-800 sm:text-lg">Pocket Money Available</p>
              <p className="text-2xl font-bold text-gray-900">
                {balanceLoading ? 'Checking...' : formatCurrency(balance)}
              </p>
            </div>
            <MyPiggyIcon name="balance" size="md" />
          </div>
          {balanceError && (
            <p className="text-sm text-red-600 mt-2">
              We could not verify your balance. Please try again before saving an expense.
            </p>
          )}
        </Card>

        {!selectedCategory ? (
          <Card className="p-6">
            <h2 className="text-xl font-extrabold text-center mb-6">
              What did you buy?
            </h2>

            <div className="grid grid-cols-2 gap-3">
              {EXPENSE_CATEGORIES.map((category) => (
                <Button
                  key={category}
                  variant="kid"
                  className="h-20 text-base flex flex-col items-center justify-center"
                  onClick={() => setSelectedCategory(category)}
                >
                  <span className="text-2xl mb-1">{getCategoryEmoji(category)}</span>
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </Button>
              ))}
            </div>
          </Card>
        ) : (
          <Card className="p-6">
            <h2 className="text-xl font-extrabold text-center mb-6 flex items-center justify-center">
              <span className="mr-2 text-2xl">{getCategoryEmoji(selectedCategory)}</span>
              {selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1)}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                label="How much did you spend?"
                type="number"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                placeholder="0.00"
                step="0.01"
                min="0.01"
                required
                className="text-lg"
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Was this a need or want?
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, needWant: 'need' })}
                    className={`p-4 rounded-xl border-2 transition-colors ${
                      formData.needWant === 'need'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <span className="text-2xl block mb-1">🍎</span>
                    <span className="font-medium">Need</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, needWant: 'want' })}
                    className={`p-4 rounded-xl border-2 transition-colors ${
                      formData.needWant === 'want'
                        ? 'border-pink-500 bg-pink-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <span className="text-2xl block mb-1">🍭</span>
                    <span className="font-medium">Want</span>
                  </button>
                </div>
              </div>

              <Input
                label="What did you buy? (optional)"
                name="note"
                value={formData.note}
                onChange={handleChange}
                placeholder="Tell us what you bought"
              />

              {error && (
                <p className="text-red-500 text-center">{error}</p>
              )}

              <div className="flex space-x-3">
                <Button
                  type="button"
                  variant="secondary"
                  className="flex-1"
                  onClick={() => {
                    setSelectedCategory('');
                    setError('');
                  }}
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  variant="kid"
                  className="flex-1"
                  disabled={
                    loading ||
                    balanceLoading ||
                    !!balanceError ||
                    !formData.needWant ||
                    isWriteLocked
                  }
                >
                  {loading ? 'Saving...' : 'Save Expense'}
                </Button>
              </div>
            </form>
          </Card>
        )}
      </div>
    </KidLayout>
  );
};

export default AddExpense;

